/**
 * User-facing error message helper.
 *
 * Phase 42 audit (F3): pre-fix several pages handled errors with
 * `alert(err.response?.data?.error || "...")`, which surfaced raw
 * backend error CODES (e.g., "quiet_hours", "already_scheduled") and
 * — worse — passed raw 500 messages straight through to users. The
 * code strings leak implementation detail; the 500 messages can leak
 * Python tracebacks or SQLAlchemy exception text on a misconfigured
 * deploy.
 *
 * This module centralizes the mapping from backend error code →
 * user-friendly message. Pages call ``errorToUserMessage(err,
 * fallback)`` instead of building the string inline. Unknown codes
 * fall through to the per-call-site fallback (e.g., "Failed to send
 * message"), which is generic and safe.
 *
 * To extend: add a new key to KNOWN_ERROR_CODES below. Don't add raw
 * 500 messages to the dictionary — those should always fall through
 * to the fallback string the caller provides.
 */

// Known backend error codes mapped to messages users can act on.
// Keys match the ``error`` field returned by the backend's 4xx JSON
// responses; values are sentence-cased English the user sees.
const KNOWN_ERROR_CODES = {
  // Auth / session
  invalid_credentials: "That email and password don't match. Please try again.",
  account_deactivated: "That email and password don't match. Please try again.",
  weak_password: "Choose a stronger password — at least 12 characters with a mix of cases, numbers, and symbols.",
  password_too_long: "Password is too long. Please choose something under 1024 characters.",

  // Compliance / sending
  quiet_hours: "Sending is paused for this campaign's quiet hours. Try again when local quiet hours end.",
  invalid_campaign_timezone: "This campaign's timezone setting is invalid. Ask an admin to update it.",
  not_in_quiet_hours: "Sending is allowed right now — use the regular send button instead of queueing.",
  already_scheduled: "This contact already has a queued message. Cancel the existing one before queueing another.",
  not_cancelable: "This message can't be canceled — it has already been sent or canceled.",

  // Authorization
  forbidden: "You don't have permission to perform that action.",

  // Data integrity / state
  invalid_period: "That time period isn't recognized. Pick today, 7d, or 30d.",
  invalid_format: "That export format isn't supported.",
};

/**
 * Map an axios error to a user-facing message.
 *
 * @param {unknown} err - the error caught from an axios call. May be an
 *   axios error, a network error, or a non-axios throwable.
 * @param {string} fallback - the per-call-site fallback to show when
 *   the error doesn't match a known code (defaults to a safe generic).
 * @returns {string} a sentence-cased message safe to render in alerts
 *   or inline error states.
 */
export function errorToUserMessage(err, fallback = "Something went wrong. Please try again.") {
  // Network error (no response) — surface a clear offline-style hint.
  if (err && !err.response) {
    if (err.code === "ECONNABORTED") return "The request timed out. Please try again.";
    if (err.code === "ERR_NETWORK") return "Can't reach the server. Check your internet connection and try again.";
  }

  const code = err?.response?.data?.error;
  if (code && Object.prototype.hasOwnProperty.call(KNOWN_ERROR_CODES, code)) {
    return KNOWN_ERROR_CODES[code];
  }

  // Unknown code — use the caller's fallback. Critically: NEVER fall
  // back to the raw `code` string itself, which could be a 500
  // exception message rather than a clean error code.
  return fallback;
}

export default errorToUserMessage;
